import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, MenuItem, Alert, CircularProgress,
    Box, Divider, FormControlLabel, Switch,
} from "@mui/material";
import { financialAccountService } from "../../services/financialAccountService";
import { FinancialAccount, FinancialAccountCreate, FinancialAccountUpdate, AccountType } from "../../types/accounts";

interface AccountFormProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    account?: FinancialAccount | null;
    mode: "create" | "edit";
}

const ACCOUNT_TYPES: { value: AccountType; label: string }[] = [
    { value: "CASH", label: "Cash" },
    { value: "BANK", label: "Bank" },
    { value: "MOBILE_WALLET", label: "Mobile Wallet" },
    { value: "LOAN", label: "Loan" },
    { value: "OTHER", label: "Other" },
];

const today = () => new Date().toISOString().split("T")[0];

export const AccountForm: React.FC<AccountFormProps> = ({ open, onClose, onSuccess, account, mode }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [form, setForm] = useState({
        name: "",
        type: "CASH" as AccountType,
        opening_balance: "0",
        opening_balance_date: today(),
        interest_rate: "",
        repayment_date: "",
        is_active: true,
        notes: "",
    });

    useEffect(() => {
        if (!open) return;
        setError("");
        if (mode === "edit" && account) {
            setForm({
                name: account.name,
                type: account.type,
                opening_balance: String(account.opening_balance),
                opening_balance_date: account.opening_balance_date,
                interest_rate: account.interest_rate != null ? String(account.interest_rate) : "",
                repayment_date: account.repayment_date || "",
                is_active: account.is_active,
                notes: account.notes || "",
            });
        } else {
            setForm({
                name: "", type: "CASH", opening_balance: "0", opening_balance_date: today(),
                interest_rate: "", repayment_date: "", is_active: true, notes: "",
            });
        }
    }, [open, mode, account]);

    const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setForm((f) => ({ ...f, [key]: e.target.value }));

    const isLoan = form.type === "LOAN";

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");

        if (!form.name.trim()) { setError("Account name is required"); return; }
        const opening = parseFloat(form.opening_balance);
        if (isNaN(opening)) { setError("Opening balance must be a number"); return; }
        const interest = form.interest_rate ? parseFloat(form.interest_rate) : undefined;
        if (form.interest_rate && (isNaN(interest!) || interest! < 0)) {
            setError("Interest rate must be a non-negative number");
            return;
        }

        try {
            setLoading(true);
            if (mode === "create") {
                const data: FinancialAccountCreate = {
                    name: form.name.trim(),
                    type: form.type,
                    opening_balance: opening,
                    opening_balance_date: form.opening_balance_date,
                    interest_rate: interest,
                    repayment_date: form.repayment_date || undefined,
                    is_active: form.is_active,
                    notes: form.notes || undefined,
                };
                await financialAccountService.createAccount(data);
            } else if (account) {
                const data: FinancialAccountUpdate = {
                    name: form.name.trim(),
                    type: form.type,
                    opening_balance: opening,
                    opening_balance_date: form.opening_balance_date,
                    interest_rate: interest,
                    repayment_date: form.repayment_date || undefined,
                    is_active: form.is_active,
                    notes: form.notes || undefined,
                };
                await financialAccountService.updateAccount(account.account_id, data);
            }
            onSuccess();
        } catch (err: any) {
            setError(err.response?.data?.detail || err.message || "Failed to save account");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>{mode === "create" ? "Add Account" : "Edit Account"}</DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                        <TextField
                            label="Account Name"
                            value={form.name}
                            onChange={set("name")}
                            required fullWidth
                            placeholder="e.g. Cash Drawer, Bank A - DBBL, Loan - XYZ Bank"
                        />

                        <TextField
                            select label="Type" value={form.type} fullWidth required
                            onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as AccountType }))}
                        >
                            {ACCOUNT_TYPES.map((t) => (
                                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                            ))}
                        </TextField>

                        <Divider />

                        <Box sx={{ display: "flex", gap: 2 }}>
                            <TextField
                                type="number" label={isLoan ? "Principal Owed (৳)" : "Opening Balance (৳)"}
                                value={form.opening_balance}
                                onChange={set("opening_balance")} required fullWidth
                                inputProps={{ step: 0.01 }}
                                helperText={isLoan ? "The amount owed as of the date below" : "Balance as of the date below"}
                            />
                            <TextField
                                type="date" label={isLoan ? "As Of" : "Opening Balance Date"}
                                value={form.opening_balance_date}
                                onChange={set("opening_balance_date")} required fullWidth
                                InputLabelProps={{ shrink: true }}
                            />
                        </Box>

                        {isLoan && (
                            <Box sx={{ display: "flex", gap: 2 }}>
                                <TextField
                                    type="number" label="Interest Rate (%, optional)"
                                    value={form.interest_rate}
                                    onChange={set("interest_rate")} fullWidth
                                    inputProps={{ min: 0, step: 0.01 }}
                                    helperText="Purely descriptive — not used in any calculation"
                                />
                                <TextField
                                    type="date" label="Repayment Date (optional)"
                                    value={form.repayment_date}
                                    onChange={set("repayment_date")} fullWidth
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Box>
                        )}

                        <Divider />

                        <TextField
                            label="Notes" value={form.notes}
                            onChange={set("notes")} fullWidth multiline rows={2}
                            placeholder="Any nuance, e.g. interest terms, account number..."
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.is_active}
                                    onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                                />
                            }
                            label="Active"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={loading}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={loading}>
                        {loading ? <CircularProgress size={22} /> : mode === "create" ? "Add Account" : "Save Changes"}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};
